import { NOT_APPLICABLE } from 'common/constants/general';
import { ValuationsApproach } from 'common/types/valuation';
import {
  SHEET_ALIASES_CONSTANTS,
  SHEET_TITLES_CONSTANTS,
} from 'pages/Valuations/approaches/guidelinePublicCompanies/PerformanceMetrics/common/constants/performanceMetrics';
import getApproachTableName from 'pages/ValuationsAllocation/util/getApproachTableName/getApproachTableName';
import { countDecimalPlaces } from 'utilities';
import {
  EvaluateNTMRevenueGrowth,
  GenerateNTMRevenueGrowthExpressionParams,
  GenerateNTMRevenueGrowthNotApplicableConditionParams,
  GeneratePerformanceMetricsRankExpressionParams,
  GeneratePerformanceMetricsRankExpressionReturn,
  GetPerformanceMetricsRankExpressionParams,
} from './types';

const { PERFORMANCE_METRICS_SPREADSHEET_COLUMNS } = SHEET_TITLES_CONSTANTS;

const { PERFORMANCE_METRICS_SPREADSHEET_COMPANY } = SHEET_ALIASES_CONSTANTS;

const currencyRankedPerformanceMetrics = new Set<number>([
  PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.LTM_REVENUE,
  PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NTM_REVENUE,
  PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.LTM_EBITDA,
  PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NTM_EBITDA,
  PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.TANGIBLE_BOOK,
  PERFORMANCE_METRICS_SPREADSHEET_COLUMNS.NET_INCOME,
]);

const getCompanyRowNumberFromRowExpression = (rowExpression: string[]): number => {
  const lastRow = rowExpression.at(-1);
  return lastRow ? Number(lastRow.slice(1)) + 1 : 0;
};

const getCompanyRowNumber = (params: GetPerformanceMetricsRankExpressionParams): number =>
  params.variant === 'calibration'
    ? params.analyzedCompanyRow
    : getCompanyRowNumberFromRowExpression(params.rowExpression);

type RankVariantHandler = {
  buildBody: (rowExpression: string[], params: GetPerformanceMetricsRankExpressionParams) => string;
};

// Controls whether a performance metrics rank cell is shown for the given column.
// Requires comparison rows and hides ranks at or after the approach-specific cutoff.
const shouldShowPerformanceMetricsRank = (params: GetPerformanceMetricsRankExpressionParams): boolean => {
  if (params.rowExpression.length === 0) {
    return false;
  }

  return params.colNumber < params.rankVisibleBeforeColumn;
};

const rankVariantHandlers: Record<string, RankVariantHandler> = {
  gpc: {
    buildBody: (rowExpression, params) => {
      if (params.variant !== 'gpc') {
        return '';
      }
      const companyRowKey = `@${getCompanyRowNumberFromRowExpression(rowExpression)}`;

      if (currencyRankedPerformanceMetrics.has(params.colNumber) && !params.marketMatchesFinancialsCurrency) {
        const exchangeRateTableName = getApproachTableName({
          approach: params.approach as ValuationsApproach,
          tableSuffix: 'exchangeRates',
        });

        return `=PERCENTRANK(FILTER_ZEROS_AND_CONVERT_CURRENCY([${rowExpression}], ${exchangeRateTableName}.marketCurrency), ${companyRowKey} * ${exchangeRateTableName}.financialsCurrency)`;
      }

      return `=PERCENTRANK(FILTER_ZEROS_AS_OBJECT([${rowExpression}]), ${companyRowKey})`;
    },
  },
  calibration: {
    buildBody: (rowExpression, params) => {
      if (params.variant !== 'calibration') {
        return '';
      }

      return params.isMultiple
        ? `=PERCENTRANK(FILTER_NON_POSITIVE_AS_OBJECT([${rowExpression}]), @${params.analyzedCompanyRow})`
        : `=PERCENTRANK(FILTER_ZEROS_AS_OBJECT([${rowExpression}]), @${params.analyzedCompanyRow})`;
    },
  },
  asOfDate: {
    buildBody: rowExpression => `=PERCENTRANK([${rowExpression}], ${rowExpression.at(-1)})`,
  },
  transactionComps: {
    buildBody: rowExpression => {
      const companyRowKey = `@${getCompanyRowNumberFromRowExpression(rowExpression)}`;

      return `=PERCENTRANK(FILTER_ZEROS_AS_OBJECT([${rowExpression}]), ${companyRowKey})`;
    },
  },
};

export const generateNTMRevenueGrowthExpression = (params: GenerateNTMRevenueGrowthExpressionParams): string => {
  const { alphabet, rowNumber, ltmRevenueColumn, ntmRevenueColumn } = params;

  // Column legend (-1 excluding Company Column)
  const columnLTMRevenueLegend = alphabet[ltmRevenueColumn - 1];
  const columnNTMRevenueLegend = alphabet[ntmRevenueColumn - 1];

  const columnLTMRevenueKey = columnLTMRevenueLegend + rowNumber;
  const columnNTMRevenueKey = columnNTMRevenueLegend + rowNumber;

  // NTM Revenue Growth formula expression
  const NTMRevenueGrowthExpression = `=IF(OR(${columnNTMRevenueKey} == 0, ${columnLTMRevenueKey} == 0), "${NOT_APPLICABLE}", (${columnNTMRevenueKey} / ${columnLTMRevenueKey}) - 1)`;

  return NTMRevenueGrowthExpression;
};

export const generateNTMRevenueGrowthNotApplicableCondition = (
  params: GenerateNTMRevenueGrowthNotApplicableConditionParams
): string => {
  const { alphabet, companyRowNumber, ltmRevenueColumn, ntmRevenueColumn } = params;

  const columnLTMRevenueKey = alphabet[ltmRevenueColumn - 1] + companyRowNumber;
  const columnNTMRevenueKey = alphabet[ntmRevenueColumn - 1] + companyRowNumber;

  return `OR(${columnNTMRevenueKey} == 0, ${columnLTMRevenueKey} == 0)`;
};

export const generatePerformanceMetricsRankExpression = (
  params: GeneratePerformanceMetricsRankExpressionParams
): GeneratePerformanceMetricsRankExpressionReturn => {
  const {
    alphabet,
    colNumber,
    companyRowNumber,
    percentRankExpression,
    companyMetricValue,
    ltmRevenueColumn,
    ntmRevenueColumn,
    ntmRevenueGrowthColumn,
  } = params;

  if (companyMetricValue === NOT_APPLICABLE) {
    return {
      expr: '',
      value: NOT_APPLICABLE,
      hidden: false,
    };
  }

  if (!percentRankExpression) {
    return {
      expr: null,
      hidden: true,
    };
  }

  if (colNumber === ntmRevenueGrowthColumn) {
    const notApplicableCondition = generateNTMRevenueGrowthNotApplicableCondition({
      alphabet,
      companyRowNumber,
      ltmRevenueColumn,
      ntmRevenueColumn,
    });
    const percentRankCore = percentRankExpression.startsWith('=')
      ? percentRankExpression.slice(1)
      : percentRankExpression;

    return {
      expr: `=IF(${notApplicableCondition}, "${NOT_APPLICABLE}", ${percentRankCore})`,
      hidden: false,
    };
  }

  return {
    expr: percentRankExpression,
    hidden: false,
  };
};

export const getPerformanceMetricsRankExpression = (
  params: GetPerformanceMetricsRankExpressionParams
): GeneratePerformanceMetricsRankExpressionReturn => {
  const handler = rankVariantHandlers[params.variant];

  if (!handler || !shouldShowPerformanceMetricsRank(params)) {
    return { expr: null, hidden: true };
  }

  return generatePerformanceMetricsRankExpression({
    alphabet: params.alphabet,
    colNumber: params.colNumber,
    companyRowNumber: getCompanyRowNumber(params),
    percentRankExpression: handler.buildBody(params.rowExpression, params),
    companyMetricValue: params.column[PERFORMANCE_METRICS_SPREADSHEET_COMPANY]?.value,
    ltmRevenueColumn: params.ltmRevenueColumn,
    ntmRevenueColumn: params.ntmRevenueColumn,
    ntmRevenueGrowthColumn: params.ntmRevenueGrowthColumn,
  });
};

export const evaluateNTMRevenueGrowth: EvaluateNTMRevenueGrowth = params => {
  const { alias, alphabet, rowNumber, ltmRevenueColumn, ntmRevenueColumn } = params;
  let { value, expr } = params;

  if (value === NOT_APPLICABLE && alias !== PERFORMANCE_METRICS_SPREADSHEET_COMPANY) {
    return {
      expr: '',
      value,
    };
  }

  // If the NTM Revenue Growth has 2 or less decimal places, we need to use the expression to display the accurate value
  // Because that means that the value is not using the new decimal places coming from the CapIQ API
  // We also need to use the expression for the company
  if (alias === PERFORMANCE_METRICS_SPREADSHEET_COMPANY || countDecimalPlaces(Number(value)) <= 2) {
    value = 0;
    expr = generateNTMRevenueGrowthExpression({ alphabet, rowNumber, ltmRevenueColumn, ntmRevenueColumn });
  }

  // The value is in percentage, we only need to apply the format
  if (alias !== PERFORMANCE_METRICS_SPREADSHEET_COMPANY) {
    value = Number(value ?? 0);
  }

  return { expr, value };
};
